import React, { useState } from "react";
import {
    TextField,
    Button,
    CircularProgress,
    Typography,
    Box,
} from "@mui/material";
import axios from "axios";
import { isEmpty, SUCCESS } from "../../../constants/Constants";

const AadharVerificationBox = ({
    BACKEND_BASE_URL,
    authUser,
    activeShopHash,
    onVerified,
}) => {
    const [aadhar, setAadhar] = useState("");
    const [otp, setOtp] = useState("");
    const [refId, setRefId] = useState("");
    const [loading, setLoading] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [message, setMessage] = useState({ text: "", color: "" });
    const [aadharLocked, setAadharLocked] = useState(false);

    const handleGenerateOtp = async () => {
        if (isEmpty(aadhar)) {
            setMessage({ text: "Please enter Aadhar ID to proceed.", color: "red" });
            return;
        }

        if (!/^\d{12}$/.test(aadhar)) {
            setMessage({ text: "Aadhar number must be 12 digits.", color: "red" });
            return;
        }

        try {
            setLoading(true);
            setMessage({ text: "", color: "" });
            const response = await axios.get(
                `${BACKEND_BASE_URL}generate-aadhar-otp`,
                {
                    params: {
                        apiToken: authUser.apiToken,
                        shopHash: activeShopHash.id,
                        aadhar: aadhar,
                    },
                }
            );

            if (response.data.status === SUCCESS) {
                const payLoad = response.data.payLoad;
                setRefId(payLoad.ref_id);
                setAadharLocked(true);
                setMessage({
                    text: payLoad.message || "OTP sent successfully!",
                    color: "green",
                });
            } else {
                setMessage({
                    text: response.data.payLoad.message || "Failed to send OTP.",
                    color: "red",
                });
            }
        } catch (error) {
            console.error("GenerateAadharOtp", error);
            setMessage({ text: "Something went wrong.", color: "red" });
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (isEmpty(otp)) {
            setMessage({ text: "Please enter OTP to continue.", color: "red" });
            return;
        }

        try {
            setOtpLoading(true);
            setMessage({ text: "", color: "" });
            const response = await axios.get(
                `${BACKEND_BASE_URL}get-cust-details-aadhar`,
                {
                    params: {
                        apiToken: authUser.apiToken,
                        shopHash: activeShopHash.id,
                        otp: otp,
                        refId: refId,
                    },
                }
            );

            if (response.data.status === SUCCESS) {
                setMessage({
                    text: "Aadhar Verified Successfully!",
                    color: "green",
                });
                if (onVerified) onVerified(response.data.payLoad);
            } else {
                setMessage({
                    text: response.data.payLoad.message || "OTP verification failed.",
                    color: "red",
                });
            }
        } catch (error) {
            console.error("VerifyAadharOtp", error);
            setMessage({ text: "Something went wrong.", color: "red" });
        } finally {
            setOtpLoading(false);
        }
    };

    return (
        <Box display="flex" flexDirection="column" gap={1} width="100%">
            {/* Aadhar Field */}
            <Box display="flex" gap={1}>
                <TextField
                    label="Enter Aadhar"
                    variant="filled"
                    size="small"
                    fullWidth
                    value={aadhar}
                    disabled={aadharLocked}
                    onChange={(e) => setAadhar(e.target.value.replace(/\D/g, ""))} // digits only
                />
                {!aadharLocked && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleGenerateOtp}
                        disabled={loading || aadhar.length !== 12}
                    >
                        {loading ? <CircularProgress size={20} /> : "Verify"}
                    </Button>
                )}
            </Box>

            {/* Message */}
            {message.text && (
                <Typography
                    variant="body2"
                    sx={{ color: message.color, fontWeight: 500 }}
                >
                    {message.text}
                </Typography>
            )}

            {/* OTP Field */}
            {aadharLocked && (
                <Box display="flex" gap={1}>
                    <TextField
                        label="Enter OTP"
                        variant="filled"
                        size="small"
                        fullWidth
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleVerifyOtp}
                        disabled={otpLoading || otp.length === 0}
                    >
                        {otpLoading ? <CircularProgress size={20} /> : "Submit"}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default AadharVerificationBox;
